<template>
  <div class="flex w-[548px] flex-col items-start gap-3 self-stretch pb-5">
    <section class="flex flex-col items-start gap-2 self-stretch">
      <UITextSmMedium>{{ t("common.iframeEmbed") }}</UITextSmMedium>
      <UIInput id="iFrameurl" :model-value="iframeUrl" type="textarea" :rows="7" readonly>
        <template #suffix
          ><Copy01Icon
            class="h-5 w-5 cursor-pointer"
            :class="activeClass"
            @click="copyLink" /></template
      ></UIInput>
    </section>
    <CookieConsentSettings
      :enabled="checkForCookieConsent"
      :provider="cookieConsentProvider"
      @update:enabled="onCookieConsentToggle"
      @update:provider="onProviderChange"
    />
  </div>
</template>

<script setup lang="ts">
import config from "@/config";
import { useAppStore } from "@/store/app";
import {
  COOKIE_CONSENT_ENABLED_DEFAULT,
  COOKIE_CONSENT_PROVIDER_DEFAULT,
} from "@/util/constants";
import { sleep, openLinkInNewTab } from "@/util/methods";
import { Copy01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIInput, UITextSmMedium } from "@gohighlevel/ghl-ui";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import CookieConsentSettings from "./CookieConsentSettings.vue";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  isWhiteLabelled: {
    type: Boolean,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  widgetName: {
    type: String,
    required: true,
  },
  widgetId: {
    type: String,
    required: true,
  },
  baseUrl: {
    type: String,
    required: true,
  },
});

const embedUrl = ref();
const iframeUrl = ref();
const activeClass = ref("text-black-600");
const checkForCookieConsent = ref(COOKIE_CONSENT_ENABLED_DEFAULT);
const cookieConsentProvider = ref<string>(COOKIE_CONSENT_PROVIDER_DEFAULT);

createEmbedUrl();

const copyLink = async () => {
  activeClass.value = "text-blue-600";
  store.copyToClipBoard(iframeUrl.value);
  await sleep(6000);
  activeClass.value = "text-black-600";
};

function createEmbedScriptUrl() {
  if (props.isWhiteLabelled && process.env.NODE_ENV !== "beta") {
    return `${props.baseUrl}/js`;
  }
  return config.FORM_EMBED_URL;
}

function getCookieConsentAttributes() {
  if (!checkForCookieConsent.value) {
    return `data-cookie-consent="false"`;
  }
  return `data-cookie-consent="true" data-cookie-consent-provider="${cookieConsentProvider.value}"`;
}

function createEmbedUrl() {
  if (process.env.NODE_ENV === "development") {
    embedUrl.value = `http://localhost:3344/widget/${props.widgetName}/${props.widgetId}`;
  } else {
    embedUrl.value = props.baseUrl + `/widget/${props.widgetName}/${props.widgetId}`;
  }

  buildIframeUrl();
}

function buildIframeUrl() {
  iframeUrl.value = `<iframe src="${
    embedUrl.value
  }" style="border:none;width:100%;" scrolling="no" id="${props.widgetId}" title="${
    props.widgetName
  }" ${getCookieConsentAttributes()}></iframe>\n<script src="${createEmbedScriptUrl()}/form_embed.js"><\/script>`;
}

const onCookieConsentToggle = (enabled: boolean) => {
  checkForCookieConsent.value = enabled;
  buildIframeUrl();
};

const onProviderChange = (value: string) => {
  cookieConsentProvider.value = value;
  buildIframeUrl();
};

const redirectToFunnel = async (url) => {
  store.copyToClipBoard(iframeUrl.value);
  await sleep(1000);
  openLinkInNewTab(url);
};
</script>
